import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { PERIOD } from '@constants/billing';
import { MONTHS_IN_YEAR, PROCESSING_FEE } from '@constants/common';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { convertFromCents } from '@pages/Settings/utils';
import { formatCurrency } from '@utils/formatCurrency';
import styled, { css, keyframes } from 'styled-components';

import { useTheme } from '@/components/theme-v2/ThemeContext';

// Animations
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

// Styled Components
const ModalOverlay = styled.div<{ $isDark?: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${props => (props.$isDark ? 'rgba(0, 0, 0, 0.7)' : 'rgba(15, 23, 42, 0.5)')};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: ${fadeIn} 0.2s ease-out;
  padding: 24px;
  box-sizing: border-box;
`;

const ModalContainer = styled.div<{ $isDark?: boolean }>`
  background: ${props =>
    props.$isDark
      ? 'linear-gradient(145deg, rgba(21, 28, 36, 0.98), rgba(15, 20, 25, 0.98))'
      : 'linear-gradient(145deg, #ffffff, #f8fafc)'};
  border: 1px solid ${props => (props.$isDark ? 'rgba(0, 200, 224, 0.3)' : 'rgba(0, 163, 184, 0.3)')};
  border-radius: 16px;
  width: 100%;
  max-width: 480px;
  animation: ${slideUp} 0.3s ease-out;
  position: relative;
  box-shadow: ${props =>
    props.$isDark ? '0 25px 50px -12px rgba(0, 0, 0, 0.5)' : '0 25px 50px -12px rgba(0, 0, 0, 0.15)'};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      ${props => (props.$isDark ? 'rgba(0, 200, 224, 0.6)' : 'rgba(0, 163, 184, 0.6)')},
      transparent
    );
  }

  &::after {
    content: '';
    position: absolute;
    top: -1px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background: ${props => (props.$isDark ? '#00c8e0' : '#00a3b8')};
    border-radius: 0 0 4px 4px;
    box-shadow: 0 0 20px ${props => (props.$isDark ? 'rgba(0, 200, 224, 0.6)' : 'rgba(0, 163, 184, 0.4)')};
  }
`;

const ModalHeader = styled.div<{ $isDark?: boolean }>`
  padding: 24px 24px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  border-bottom: 1px solid ${props => (props.$isDark ? 'rgba(42, 53, 68, 0.6)' : 'rgba(226, 232, 240, 0.8)')};
`;

const BackButton = styled.button<{ $isDark?: boolean }>`
  width: 36px;
  height: 36px;
  border: 1px solid ${props => (props.$isDark ? 'rgba(148, 163, 184, 0.3)' : 'rgba(148, 163, 184, 0.4)')};
  border-radius: 8px;
  background: ${props => (props.$isDark ? 'rgba(42, 53, 68, 0.4)' : 'rgba(241, 245, 249, 0.8)')};
  color: ${props => (props.$isDark ? '#94a3b8' : '#64748b')};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${props => (props.$isDark ? '#94a3b8' : '#64748b')};
    color: ${props => (props.$isDark ? '#f0f4f8' : '#171717')};
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const ModalTitle = styled.h2<{ $isDark?: boolean }>`
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 1.25rem;
  font-weight: 600;
  color: ${props => (props.$isDark ? '#f0f4f8' : '#171717')};
  margin: 0;
  flex: 1;
  text-align: center;
  padding-right: 48px;

  span {
    color: ${props => (props.$isDark ? '#00c8e0' : '#00a3b8')};
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const TokensBadge = styled.div<{ $isDark?: boolean }>`
  background: ${props => (props.$isDark ? 'rgba(0, 200, 224, 0.1)' : 'rgba(0, 163, 184, 0.08)')};
  border: 1px solid ${props => (props.$isDark ? 'rgba(0, 200, 224, 0.2)' : 'rgba(0, 163, 184, 0.2)')};
  border-radius: 12px;
  padding: 16px;
  text-align: center;
  margin-bottom: 20px;

  span {
    font-family: 'JetBrains Mono', monospace;
    font-size: 1.25rem;
    font-weight: 600;
    color: ${props => (props.$isDark ? '#00c8e0' : '#00a3b8')};
  }
`;

const PriceRow = styled.div<{ $isDark?: boolean; $isTotal?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: ${props =>
    props.$isTotal ? 'none' : `1px solid ${props.$isDark ? 'rgba(42, 53, 68, 0.4)' : 'rgba(226, 232, 240, 0.6)'}`};

  ${props =>
    props.$isTotal &&
    css`
      margin-top: 8px;
      padding-top: 16px;
      border-top: 2px solid ${props.$isDark ? 'rgba(0, 200, 224, 0.3)' : 'rgba(0, 163, 184, 0.3)'};
    `}
`;

const PriceLabel = styled.span<{ $isDark?: boolean; $isTotal?: boolean }>`
  font-family: 'JetBrains Mono', monospace;
  font-size: ${props => (props.$isTotal ? '1rem' : '0.875rem')};
  font-weight: ${props => (props.$isTotal ? '600' : '500')};
  color: ${props => (props.$isDark ? '#94a3b8' : '#525252')};
`;

const PriceValue = styled.span<{ $isDark?: boolean; $isTotal?: boolean }>`
  font-family: 'JetBrains Mono', monospace;
  font-size: ${props => (props.$isTotal ? '1.25rem' : '0.875rem')};
  font-weight: ${props => (props.$isTotal ? '700' : '500')};
  color: ${props => (props.$isTotal ? (props.$isDark ? '#00c8e0' : '#00a3b8') : props.$isDark ? '#f0f4f8' : '#171717')};
`;

const FeaturesSection = styled.div<{ $isDark?: boolean }>`
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid ${props => (props.$isDark ? 'rgba(42, 53, 68, 0.6)' : 'rgba(226, 232, 240, 0.8)')};
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
`;

const FeatureItem = styled.div<{ $isDark?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8rem;
  color: ${props => (props.$isDark ? '#94a3b8' : '#525252')};

  svg {
    width: 14px;
    height: 14px;
    color: ${props => (props.$isDark ? '#00ff7f' : '#16a34a')};
    flex-shrink: 0;
  }
`;

const ModalFooter = styled.div<{ $isDark?: boolean }>`
  padding: 16px 24px 24px;
  display: flex;
  gap: 12px;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary'; $isDark?: boolean }>`
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.875rem;
  font-weight: 600;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  flex: 1;

  ${props =>
    props.$variant === 'primary'
      ? css<{ $isDark?: boolean }>`
          background: linear-gradient(
            135deg,
            ${p => (p.$isDark ? '#00c8e0' : '#00a3b8')},
            ${p => (p.$isDark ? '#007d8f' : '#0e7490')}
          );
          border: none;
          color: ${p => (p.$isDark ? '#0a0e14' : '#ffffff')};

          &:hover {
            background: linear-gradient(
              135deg,
              ${p => (p.$isDark ? '#41e2f7' : '#22d3ee')},
              ${p => (p.$isDark ? '#00c8e0' : '#00a3b8')}
            );
            box-shadow: 0 0 20px ${p => (p.$isDark ? 'rgba(0, 200, 224, 0.4)' : 'rgba(0, 163, 184, 0.3)')};
            transform: translateY(-1px);
          }
        `
      : css<{ $isDark?: boolean }>`
          background: transparent;
          border: 1px solid ${p => (p.$isDark ? 'rgba(148, 163, 184, 0.3)' : 'rgba(148, 163, 184, 0.4)')};
          color: ${p => (p.$isDark ? '#94a3b8' : '#64748b')};

          &:hover {
            border-color: ${p => (p.$isDark ? '#94a3b8' : '#64748b')};
            color: ${p => (p.$isDark ? '#f0f4f8' : '#171717')};
          }
        `}
`;

// Icons
const ArrowLeftIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M19 12H5M12 19l-7-7 7-7" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const BillingConfirmModal = NiceModal.create(
  ({
    price,
    tokens,
    period,
    description,
    isChangePlan,
  }: {
    price: number;
    tokens: string;
    period: string;
    description: string[];
    isChangePlan?: boolean;
  }) => {
    const { t } = useTranslation();
    const modal = useModal();
    const { theme } = useTheme();
    const isDarkMode = theme === 'dark';

    const handleConfirmClick = () => {
      modal.resolve(true);
      modal.remove();
    };

    const handleCancelClick = () => {
      modal.resolve(false);
      modal.remove();
    };

    const handleOverlayClick = (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        handleCancelClick();
      }
    };

    // Handle escape key
    useEffect(() => {
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') handleCancelClick();
      };

      if (modal.visible) {
        document.addEventListener('keydown', handleEscape);
        document.body.style.overflow = 'hidden';
      }

      return () => {
        document.removeEventListener('keydown', handleEscape);
        document.body.style.overflow = '';
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modal.visible]);

    // Calculate prices
    const basePrice = isChangePlan
      ? convertFromCents(period === PERIOD.year ? price / MONTHS_IN_YEAR : price)
      : convertFromCents(price / MONTHS_IN_YEAR);

    const processingFee = isChangePlan
      ? convertFromCents(price * PROCESSING_FEE)
      : convertFromCents((price / MONTHS_IN_YEAR) * PROCESSING_FEE);

    const totalPrice = isChangePlan
      ? convertFromCents(price * (1 + PROCESSING_FEE))
      : convertFromCents((price / MONTHS_IN_YEAR) * (1 + PROCESSING_FEE));

    if (!modal.visible) return null;

    return createPortal(
      <ModalOverlay onClick={handleOverlayClick} $isDark={isDarkMode}>
        <ModalContainer role="dialog" aria-modal="true" $isDark={isDarkMode}>
          <ModalHeader $isDark={isDarkMode}>
            <BackButton onClick={handleCancelClick} aria-label="Go back" $isDark={isDarkMode}>
              <ArrowLeftIcon />
            </BackButton>
            <ModalTitle $isDark={isDarkMode}>
              Order <span>Preview</span>
            </ModalTitle>
          </ModalHeader>

          <ModalBody>
            <TokensBadge $isDark={isDarkMode}>
              <span>
                {tokens} {t('billing.tokensPerMonth')}
              </span>
            </TokensBadge>

            {!isChangePlan && (
              <PriceRow $isDark={isDarkMode}>
                <PriceLabel $isDark={isDarkMode}>{t('billing.singleTopupPrice', 'Single top-up price')}</PriceLabel>
                <PriceValue $isDark={isDarkMode}>{formatCurrency(basePrice)}</PriceValue>
              </PriceRow>
            )}

            {isChangePlan && (
              <PriceRow $isDark={isDarkMode}>
                <PriceLabel $isDark={isDarkMode}>{t('billing.subscriptionPrice', 'Subscription price')}</PriceLabel>
                <PriceValue $isDark={isDarkMode}>{formatCurrency(basePrice)}</PriceValue>
              </PriceRow>
            )}

            <PriceRow $isDark={isDarkMode}>
              <PriceLabel $isDark={isDarkMode}>{t('billing.processingFee')}</PriceLabel>
              <PriceValue $isDark={isDarkMode}>{formatCurrency(processingFee)}</PriceValue>
            </PriceRow>

            <PriceRow $isDark={isDarkMode} $isTotal>
              <PriceLabel $isDark={isDarkMode} $isTotal>
                {t('billing.totalPrice')}
              </PriceLabel>
              <PriceValue $isDark={isDarkMode} $isTotal>
                {formatCurrency(totalPrice)}
              </PriceValue>
            </PriceRow>

            {description.length > 0 && (
              <FeaturesSection $isDark={isDarkMode}>
                <FeaturesGrid>
                  {description.map(line => (
                    <FeatureItem key={line} $isDark={isDarkMode}>
                      <CheckIcon />
                      {line}
                    </FeatureItem>
                  ))}
                </FeaturesGrid>
              </FeaturesSection>
            )}
          </ModalBody>

          <ModalFooter $isDark={isDarkMode}>
            <Button $variant="secondary" onClick={handleCancelClick} $isDark={isDarkMode}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button $variant="primary" onClick={handleConfirmClick} $isDark={isDarkMode}>
              {t('common.confirm', 'Confirm')} - {formatCurrency(totalPrice)}
            </Button>
          </ModalFooter>
        </ModalContainer>
      </ModalOverlay>,
      document.body,
    );
  },
);

export const BillingConfirmModalId = 'BillingConfirmModal';

NiceModal.register(BillingConfirmModalId, BillingConfirmModal);
